import { useRef } from 'react';
import { useHistory } from 'react-router-dom';

import styled from 'styled-components';

import { type Invitation } from '@sine/sine-core-api-types';
import { Button, ButtonGroup, type JoiForm, Loader, Modal } from '@sine/sine-ui-components';

import type { InitialInvitationValues, InvitationFormRef } from '../../Invitations.types';
import { useInvitationSiteOptions } from '../../hooks/useInvitationSiteOptions';
import { CreateInvitationForm } from './CreateInvitationForm';
import { NoSitesSplash } from './NoSitesSplash';

interface CreateInvitationModalProps {
  onClose: VoidFunction;
  invitation: Invitation | undefined;
}

const StyledModal = styled(Modal)`
  div[class*='Modal__ModalOuterContainer'] {
    margin-top: auto;
    margin-bottom: auto;
  }

  div[data-st-id='modal-body'] {
    @media only screen and (min-width: 1200px) {
      height: 75vh;
    }
    overflow-y: auto;
  }
`;

export function CreateInvitationModal({ onClose, invitation }: CreateInvitationModalProps) {
  const { allSites, siteOptions, isLoadingInvitationSiteOptions } = useInvitationSiteOptions();

  const history = useHistory();

  const formRef = useRef<InvitationFormRef>(null);
  const inviteesRef = useRef<{ getForms: () => Record<string, JoiForm<InitialInvitationValues['invitee']>> }>(null);

  const handleSubmit = () => {
    if (invitation) {
      formRef.current?.handleUpdateInvitation(invitation);
    } else {
      formRef.current?.handleCreateInvitations();
    }
  };

  return allSites.length && !isLoadingInvitationSiteOptions ? (
    <StyledModal
      show
      size='xl'
      onClose={onClose}
      header={invitation ? 'Edit invitation' : 'Create invitation'}
      footer={
        <ButtonGroup alignment='right'>
          <Button variant='tertiary' onClick={onClose} data-st-ext='button-cancel'>
            Cancel
          </Button>
          <Button variant='primary' onClick={handleSubmit} data-st-ext='button-create'>
            {invitation ? 'Save invitation' : 'Create invitation'}
          </Button>
        </ButtonGroup>
      }
    >
      <Loader fillAndCenter overlay show={isLoadingInvitationSiteOptions} />
      <CreateInvitationForm
        invitation={invitation}
        allSites={allSites}
        siteOptions={siteOptions}
        ref={formRef}
        inviteesRef={inviteesRef}
        onClose={onClose}
      />
    </StyledModal>
  ) : (
    <Modal
      show
      size='xs'
      onClose={onClose}
      footer={
        <ButtonGroup>
          <Button variant='tertiary' onClick={onClose}>
            Close
          </Button>
          <Button
            variant='primary'
            onClick={() => {
              history.push('/locations/sites');
              onClose();
            }}
          >
            Go to sites
          </Button>
        </ButtonGroup>
      }
    >
      <NoSitesSplash />
    </Modal>
  );
}
